import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../shared/ui/primitives';
import { UI_LOCALE_NAMES, useLocaleStore, type UiLocale } from './index';

/**
 * A small flag beside the code. Drawn inline: Windows has no flag emoji at
 * all, so an emoji flag renders as two letters there. Every Indian language
 * shares the tricolour; English gets the Union Jack.
 */
function Flag({ code }: { code: UiLocale }): React.JSX.Element {
  if (code === 'en') {
    return (
      <svg viewBox="0 0 24 16" className="h-3 w-[18px] shrink-0 rounded-[2px]" aria-hidden>
        <rect width="24" height="16" fill="#1e3a8a" />
        <path d="M0 0l24 16M24 0L0 16" stroke="#fff" strokeWidth="3" />
        <path d="M0 0l24 16M24 0L0 16" stroke="#dc2626" strokeWidth="1.2" />
        <path d="M12 0v16M0 8h24" stroke="#fff" strokeWidth="5" />
        <path d="M12 0v16M0 8h24" stroke="#dc2626" strokeWidth="2.6" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 16" className="h-3 w-[18px] shrink-0 rounded-[2px]" aria-hidden>
      <rect width="24" height="5.34" fill="#f97316" />
      <rect y="5.33" width="24" height="5.34" fill="#fff" />
      <rect y="10.66" width="24" height="5.34" fill="#16a34a" />
      <circle cx="12" cy="8" r="2" fill="none" stroke="#1e3a8a" strokeWidth="0.8" />
    </svg>
  );
}

/**
 * The app bar's language picker. Compact when closed — a flag and the
 * two-letter code, no wasted width — and the full native names in the list.
 * Only the languages Platform → General enabled are offered.
 */
export function LanguageSwitch({ locale, enabled, className }: { locale: UiLocale; enabled: UiLocale[]; className?: string }): React.JSX.Element | null {
  const setLocale = useLocaleStore((state) => state.setLocale);
  const [open, setOpen] = React.useState(false);
  if (enabled.length < 2) return null;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`Language: ${UI_LOCALE_NAMES[locale].name}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-sk-md border border-outline-field bg-surface px-2.5 text-[13px] font-semibold text-ink-1 transition-colors hover:border-indigo/60 focus-visible:border-indigo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo/25',
            className,
          )}
        >
          <Flag code={locale} />
          <span className="uppercase">{locale}</span>
          <ChevronDown className="size-3.5 text-ink-3" aria-hidden />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={4} collisionPadding={12} className="z-[60] min-w-[11rem] rounded-sk-lg border border-outline bg-surface p-1 shadow-sk-lg focus-visible:outline-none">
          <ul role="listbox" aria-label="Language">
            {enabled.map((code) => {
              const active = code === locale;
              return (
                <li key={code} role="option" aria-selected={active}>
                  <button
                    type="button"
                    onClick={() => {
                      setLocale(code);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-sk-md px-2.5 py-2 text-left text-[13px] hover:bg-surface-variant',
                      active ? 'font-semibold text-indigo-ink' : 'text-ink-1',
                    )}
                  >
                    <Flag code={code} />
                    <span className="min-w-0 flex-1">
                      <span className="block">{UI_LOCALE_NAMES[code].native}</span>
                      <span className="block text-2xs text-ink-3">{UI_LOCALE_NAMES[code].name}</span>
                    </span>
                    {active ? <Check className="size-4 text-indigo" aria-hidden /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
